/**
 * Il programma di un evento visto dalle schermate di Pixel: come si ricompone
 * dalle tabelle e come si legge un percorso (giorno, turno, aula, titolo).
 */

import { sessionePerCodice } from '@eventi/regole.ts';
import type {
  AulaEvento,
  GiornoEvento,
  Percorso,
  ProgrammaEvento,
  SessioneEvento,
  TurnoEvento,
} from '@eventi/tipi.ts';

const comeElenco = <T>(valore: unknown): T[] => (Array.isArray(valore) ? (valore as T[]) : []);

/**
 * Rimette insieme il programma: giorni e turni stanno nella riga dell'evento,
 * aule e sessioni nelle loro tabelle. Turni in ordine, sessioni per giorno e
 * orario, cosi' i menu e il tabellone escono sempre nello stesso ordine.
 */
export function costruisciProgramma(
  evento: { giorni: unknown; turni: unknown },
  aule: AulaEvento[],
  sessioni: SessioneEvento[],
): ProgrammaEvento {
  return {
    versione: 1,
    giorni: comeElenco<GiornoEvento>(evento.giorni),
    turni: [...comeElenco<TurnoEvento>(evento.turni)].sort((a, b) => a.n - b.n),
    aule: [...aule],
    sessioni: sessioni
      .map((s) => ({ ...s, turni: comeElenco<number>(s.turni), relatori: s.relatori ?? '' }))
      .sort(
        (a, b) =>
          a.giorno.localeCompare(b.giorno) ||
          a.inizio.localeCompare(b.inizio) ||
          a.aula.localeCompare(b.aula, 'it', { numeric: true }),
      ),
  };
}

/** Una riga del percorso: un turno di un giorno e cosa c'e' dentro. */
export interface RigaPercorso {
  giorno: string;
  giornoNome: string;
  turno: number;
  orario: string;
  /** Il codice scelto, o null se il turno e' vuoto. */
  codice: string | null;
  aula: string | null;
  titolo: string | null;
  /** Vero se il codice non c'e' piu' nel programma (programma reimportato). */
  mancante: boolean;
}

/** Il percorso in righe, una per ogni turno di ogni giorno (anche quelli vuoti). */
export function righePercorso(programma: ProgrammaEvento, percorso: Percorso | null | undefined): RigaPercorso[] {
  const perCodice = sessionePerCodice(programma);
  const aule = new Map(programma.aule.map((a) => [a.codice, a.nome]));
  const righe: RigaPercorso[] = [];
  for (const g of programma.giorni) {
    for (const t of programma.turni) {
      const codice = percorso?.[g.codice]?.[String(t.n)] ?? null;
      const s = codice ? perCodice.get(codice) : undefined;
      righe.push({
        giorno: g.codice,
        giornoNome: g.nome,
        turno: t.n,
        orario: `${t.inizio}–${t.fine}`,
        codice,
        aula: s ? aule.get(s.aula) ?? s.aula : null,
        titolo: s ? s.titolo : null,
        mancante: Boolean(codice && !s),
      });
    }
  }
  return righe;
}

/** Il nome dell'aula di una sessione, o il codice se l'aula non si trova. */
export function nomeAula(programma: ProgrammaEvento, codiceAula: string): string {
  return programma.aule.find((a) => a.codice === codiceAula)?.nome ?? codiceAula;
}

/** "turni 1–4" per una sessione lunga, "" per una di un turno solo. */
export function durataTurni(sessione: Pick<SessioneEvento, 'turni'>): string {
  if (sessione.turni.length <= 1) return '';
  return `turni ${Math.min(...sessione.turni)}–${Math.max(...sessione.turni)}`;
}

/** Vero se il programma ha almeno un giorno, un turno e una sessione sceglibile. */
export function programmaPronto(programma: ProgrammaEvento): boolean {
  return programma.giorni.length > 0 && programma.turni.length > 0 && programma.sessioni.some((s) => s.sceglibile);
}
